import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import axios from '@/lib/API/client'
import fetcher from '@/lib/API/fetcher'
import useSWRV, { mutate } from 'swrv'
import { sleep } from '@/lib/helpers/time'
import { getTotalWeight } from '@/lib/helpers/session'
import tailwind from '@/lib/tailwind'
import { isAccessDenied, sessionAccessStateFromError } from '@/lib/session-access-request'
import { answerInvitation } from '@/lib/hooks/use-invitations'
import { KIND_REQUEST } from '@/lib/invitations'

export default () => {
    const route = useRoute()
    const strideBeforeSelected = ref('10')
    const strideAfterSelected = ref('10')
    const sessionId = ref('')
    const page = ref('')
    const horseId = ref('')
    const riderId = ref('')
    const sessionType = ref('')
    const sessionDiscipline = ref('')

    const buildPath3dUrl = (id) => `/sessions/${id}/path3d`

    const hasSegmentSamples = (segments) =>
        Array.isArray(segments) && segments.some((segment) => Array.isArray(segment?.samples) && segment.samples.length)

    const isSnapshotPopulated = (snapshot) => {
        if (!snapshot || typeof snapshot !== 'object') return false
        if (hasSegmentSamples(snapshot.strides)) return true
        if (hasSegmentSamples(snapshot.jumps)) return true
        return false
    }

    const fetchSnapshotFromPath = async (snapshot) => {
        const path = snapshot?.snapshot_path
        if (!path || typeof path !== 'string') {
            return null
        }

        const normalisedPath = path.startsWith('http') ? path : `/${path.replace(/^\/+/, '')}`

        try {
            const response = await axios.get(normalisedPath, { responseType: 'json' })
            if (response?.data && typeof response.data === 'object') {
                return response.data
            }
        } catch (error) {
            console.warn('[useSessions] Failed to download path3d snapshot payload', normalisedPath, error)
        }
        return null
    }

    const mergeSnapshotPayloads = (baseSnapshot, supplementalSnapshot) => {
        if (!supplementalSnapshot) return baseSnapshot

        const merged = { ...baseSnapshot, ...supplementalSnapshot }

        if (Array.isArray(supplementalSnapshot.strides)) merged.strides = supplementalSnapshot.strides
        if (Array.isArray(supplementalSnapshot.jumps)) merged.jumps = supplementalSnapshot.jumps
        if (Array.isArray(supplementalSnapshot.gait_transitions)) {
            merged.gait_transitions = supplementalSnapshot.gait_transitions
        }
        if (supplementalSnapshot.samples && !merged.samples) {
            merged.samples = supplementalSnapshot.samples
        }
        if (typeof supplementalSnapshot.strides_precision === 'number') {
            merged.strides_precision = supplementalSnapshot.strides_precision
        }
        if (typeof supplementalSnapshot.jumps_precision === 'number') {
            merged.jumps_precision = supplementalSnapshot.jumps_precision
        }

        return merged
    }

    const expandSnapshotPayload = (payload) => {
        if (!payload || typeof payload !== 'object') return payload
        if (!payload.mapping || !payload.data) return payload

        try {
            const decoder = createSnapshotDecoder(payload.mapping)
            const expanded = decoder(payload.data)
            expanded._mapping = payload.mapping
            return expanded
        } catch (error) {
            console.warn('[useSessions] Failed to expand mapped snapshot payload', error)
            return payload.data ?? payload
        }
    }

    const createSnapshotDecoder = (mappingDef) => {
        const keyMaps = mappingDef?.keys || {}
        const valueMaps = mappingDef?.values || {}

        const remapObject = (source, dictionary = {}, resolver) => {
            if (!source || typeof source !== 'object' || Array.isArray(source)) {
                return source
            }

            const result = {}
            for (const [shortKey, rawValue] of Object.entries(source)) {
                const key = dictionary[shortKey] || shortKey
                result[key] = resolver ? resolver(key, rawValue) : rawValue
            }
            return result
        }

        const mapValue = (dictionary, value) => {
            if (!dictionary || value === undefined || value === null) return value
            return dictionary[value] ?? value
        }

        const decodePosition = (value) => {
            if (Array.isArray(value)) {
                const [x, y, z] = value
                return { x, y, z }
            }
            return remapObject(value, keyMaps.position || {})
        }

        const decodeSample = (sample) =>
            remapObject(sample, keyMaps.sample || {}, (key, raw) => {
                if (key === 'position') return decodePosition(raw)
                if (key === 'gait') return mapValue(valueMaps.gait, raw)
                return raw
            })

        const decodeSegment = (segment) =>
            remapObject(segment, keyMaps.segment || {}, (key, raw) => {
                if (key === 'samples') {
                    return Array.isArray(raw) ? raw.map((item) => decodeSample(item)) : []
                }
                if (key === 'gait') {
                    return mapValue(valueMaps.gait, raw)
                }
                if (key === 'source') {
                    return remapObject(raw, keyMaps.source || {})
                }
                return raw
            })

        const decodeRoot = (input) =>
            remapObject(input, keyMaps.root || {}, (key, raw) => {
                if (key === 'source') return remapObject(raw, keyMaps.source || {})
                if (key === 'strides' || key === 'jumps') {
                    return Array.isArray(raw) ? raw.map((item) => decodeSegment(item)) : []
                }
                if (key === 'gait') return mapValue(valueMaps.gait, raw)
                return raw
            })

        return (data) => decodeRoot(data)
    }

    const fetchPath3dSnapshotData = async (id) => {
        try {
            let snapshot = await fetcher(buildPath3dUrl(id))
            snapshot = expandSnapshotPayload(snapshot)
            if (!snapshot) return null

            if (isSnapshotPopulated(snapshot)) {
                return snapshot
            }

            const supplemental = await fetchSnapshotFromPath(snapshot)
            if (!supplemental) {
                return snapshot
            }

            return mergeSnapshotPayloads(snapshot, expandSnapshotPayload(supplemental))
        } catch (error) {
            if (error?.response?.status === 404) {
                return null
            }
            throw error
        }
    }

    /**
     *
     * use* methods
     *
     **/

    const { data: sessions } = useSWRV(
        () => {
            //old way to fetch the sessions list
            // return `/user/sessions?page=${page.value}&horse=${horseId.value}&rider=${riderId.value}&type=${sessionType.value}`
            // currently only use in the compare session page
            return `/user/sessions`
        },
        fetcher,
        {}
    )

    const prefetchSession = async (id) => {
        const urls = [
            `/sessions/${id}`,
            `/sessions/${id}/summary`,
            `/sessions/${id}/statistics`,
            `/sessions/${id}/streams/heartrate`,
            `/sessions/${id}/strides`,
            `/sessions/${id}/strides/statistics`,
            `/sessions/${id}/jumps`,
            `sessions/${id}/jumps/trajectories?before=${strideBeforeSelected.value}&after=${strideAfterSelected.value}`,
        ]
        for (let index = 0; index < urls.length; index++) {
            const url = urls[index]
            await mutate(url, fetcher(url, undefined, { forceNetwork: true }))
            await sleep(100)
            // the second parameter is a Promise
            // SWRV will use the result when it resolves
        }
    }

    const { data: lastSession } = useSWRV(
        () => {
            return '/user/sessions/latest'
        },
        (url) =>
            fetcher(url).then((data) => {
                //we wait 1s in order to leave the others request to be finished
                if (data.id) {
                    setTimeout(() => prefetchSession(data.id), 2000)
                }
                return data
            }),
        { revalidateOnFocus: false, dedupingInterval: 180000 }
    )

    const { data: session, error } = useSWRV(() => (sessionId.value ? `/sessions/${sessionId.value}` : null), fetcher, {
        revalidateOnFocus: false,
        dedupingInterval: 60000,
        // A refusal stays a refusal: asking again would only bring back an older answer.
        shouldRetryOnError: (failure) => !isAccessDenied(failure),
    })

    // S5: the reader may not see this session (403). accessInfo is what they may do about it
    // (null when they may ask for nothing). Nothing else of the session is requested meanwhile.
    const accessDenied = computed(() => isAccessDenied(error.value))
    const accessInfo = computed(() => sessionAccessStateFromError(error.value))
    const readableSessionId = computed(() => (accessDenied.value ? '' : sessionId.value))
    // A copy read before the access was withdrawn is no longer shown.
    watch(accessDenied, (denied) => {
        if (denied) session.value = undefined
    })

    const path3dAvailable = computed(() => !accessDenied.value && !!session.value?.path3d_available)

    const { data: path3dSnapshotRaw, mutate: mutatePath3dSnapshot } = useSWRV(
        () => (sessionId.value && path3dAvailable.value ? buildPath3dUrl(sessionId.value) : null),
        () => fetchPath3dSnapshotData(sessionId.value),
        { revalidateOnFocus: false }
    )

    const path3dSnapshot = computed(() => expandSnapshotPayload(path3dSnapshotRaw.value))

    const refreshPath3dSnapshot = () => mutatePath3dSnapshot()

    watch(
        () => path3dAvailable.value,
        (available) => {
            const id = sessionId.value
            if (!id) return
            if (!available) {
                mutate(buildPath3dUrl(id), null)
            }
        }
    )

    const { data: jumpsStatistics } = useSWRV(
        () => (readableSessionId.value ? `/sessions/${readableSessionId.value}/summary` : null),
        fetcher,
        {
            revalidateOnFocus: false,
            dedupingInterval: 60000,
        }
    )

    const { data: sessionStatistics, mutate: refreshSessionStatistics } = useSWRV(
        () => (readableSessionId.value ? `/sessions/${readableSessionId.value}/statistics` : null),
        fetcher,
        {
            revalidateOnFocus: false,
            dedupingInterval: 60000,
        }
    )

    const { data: heartrateStream, mutate: refreshHeartrateStream } = useSWRV(
        () => (readableSessionId.value ? `/sessions/${readableSessionId.value}/streams/heartrate` : null),
        fetcher,
        {
            revalidateOnFocus: true,
            dedupingInterval: 5000,
        }
    )

    const { data: strides } = useSWRV(
        () => (readableSessionId.value ? `/sessions/${readableSessionId.value}/strides` : null),
        fetcher,
        {
            revalidateOnFocus: false,
            dedupingInterval: 60000,
        }
    )

    const { data: stridesStatistics } = useSWRV(
        () => (readableSessionId.value ? `/sessions/${readableSessionId.value}/strides/statistics` : null),
        fetcher,
        { revalidateOnFocus: false, dedupingInterval: 60000 }
    )

    const { data: rawJumps } = useSWRV(
        () => (readableSessionId.value ? `/sessions/${readableSessionId.value}/jumps` : null),
        fetcher,
        {
            revalidateOnFocus: false,
            dedupingInterval: 60000,
        }
    )

    const palette = tailwind.theme.color.palette
    const colorsLength = Object.keys(palette).length

    const jumps = computed(() => {
        if (!rawJumps.value) return []

        return rawJumps.value.map((jump, index) => {
            const colorIndex = (index % colorsLength) + 1
            return {
                ...jump,
                color: palette[colorIndex],
            }
        })
    })

    const { data: trajectories } = useSWRV(
        () =>
            readableSessionId.value
                ? `sessions/${readableSessionId.value}/jumps/trajectories?before=${strideBeforeSelected.value}&after=${strideAfterSelected.value}`
                : null,
        fetcher,
        { revalidateOnFocus: false, dedupingInterval: 60000 }
    )

    const { data: advices, isValidating: isValidatingAdvices, isLoading: isLoadingAdvices } = useSWRV(
        () => (readableSessionId.value ? `sessions/${readableSessionId.value}/advices` : null),
        fetcher,
        {
            revalidateOnFocus: false,
            dedupingInterval: 60000,
        }
    )

    watch(
        [
            () => route.params.sessionId,
            () => route.query.page,
            () => route.query.horse,
            () => route.query.rider,
            () => route.query.type,
            () => route.query.discipline,
            () => route.query.year,
            () => route.query.month,
        ],
        async (
            [newSessionId, newPage, newHorseId, newRiderId, newSessionType, newSessionDiscipline, newYear, newMonth],
            [oldSessionId, oldPage, oldHorseId, oldRiderId, oldSessionType, oldSessionDiscipline, oldYear, oldMonth]
        ) => {
            // console.log(
            //     [newSessionId, newPage, newHorseId, newRiderId, newSessionType],
            //     [oldSessionId, oldPage, oldHorseId, oldRiderId, oldSessionType]
            // )

            page.value = newPage ?? ''
            horseId.value = newHorseId ?? oldHorseId ?? ''
            riderId.value = newRiderId ?? oldRiderId ?? ''
            sessionType.value = newSessionType ?? oldSessionType ?? ''
            sessionDiscipline.value = newSessionDiscipline ?? oldSessionDiscipline ?? ''

            if (oldYear !== newYear) year.value = parseInt(newYear)
            if (oldMonth !== newMonth) month.value = parseInt(newMonth)

            if (newSessionId !== oldSessionId) {
                session.value = undefined
                error.value = undefined // a refusal belongs to the previous session
                trajectories.value = undefined
                stridesStatistics.value = undefined
                strides.value = undefined
                jumpsStatistics.value = undefined
                sessionStatistics.value = undefined
                heartrateStream.value = undefined
                rawJumps.value = undefined
                advices.value = undefined

                //await new Promise((resolve) => setTimeout(resolve, 100)) //wait 100ms in order to have a transition / loader
            }
            sessionId.value = newSessionId ?? oldSessionId ?? '' //will trigger the query of all the others endpoint
        },
        { immediate: true }
    )

    /**
     *
     * mutate* methods
     *
     **/
    const mutateSessions = (page) => {
        mutate(
            `/user/sessions?page=${page.value}`,
            fetcher(`/user/sessions?page=${page.value}`, undefined, { forceNetwork: true })
        )
    }

    const mutateSession = (sessionId, data) => {
        mutate(
            `/sessions/${sessionId}`,
            data ? data : fetcher(`/sessions/${sessionId}`, undefined, { forceNetwork: true })
        )
    }

    const createSession = (data) => {
        return axios.post('/sessions', data).then((response) => {
            mutateSession(response.data.id, response.data)
            mutate('/sessions', fetcher('/sessions', undefined, { forceNetwork: true }))
            mutate('/user/sessions', fetcher('/user/sessions', undefined, { forceNetwork: true }))
            return response.data
        })
    }

    const checkPath3dAvailability = async (_sessionId = sessionId.value, { forceRefresh = true } = {}) => {
        if (!_sessionId) {
            return { available: false }
        }

        if (_sessionId === sessionId.value) {
            let data = session.value
            if (forceRefresh || !data) {
                data = await fetcher(`/sessions/${_sessionId}`)
                mutate(`/sessions/${_sessionId}`, data)
            }
            return { available: !!data?.path3d_available }
        }

        const data = await fetcher(`/sessions/${_sessionId}`)
        return { available: !!data?.path3d_available }
    }

    const fetchPath3dSnapshot = async (_sessionId = sessionId.value) => {
        if (!_sessionId) {
            throw new Error('Missing sessionId for path3d snapshot fetch')
        }

        const data = await fetchPath3dSnapshotData(_sessionId)

        if (_sessionId === sessionId.value) {
            await mutate(buildPath3dUrl(_sessionId), data)
        }

        return data
    }

    /**
     *
     * fetch* methods
     *
     **/

    /**
     *
     * internal methods
     *
     **/
    const changeGaitOfSelection = (gait, timeRange) => {
        for (let index = 0; index < strides.value?.length; index++) {
            const stride = strides.value[index]
            if (stride.pos.time > timeRange[1]) {
                break //stop the loop because we are outside the frame
            }
            if (stride.pos.time > timeRange[0]) {
                stride.gait = gait
                stride.locallyEdited = true
                // console.log('inside', stride.pos.time)
            }
        }
    }
    const updateStride = ({ index, stride, statistics }) => {
        strides.value[index] = stride
        strides.value[index].locallyEdited = true
        stridesStatistics.value[index] = statistics
    }

    /**
     *
     * API methods
     *
     **/

    const updateSession = (_sessionId, data) => {
        return axios.put(`/sessions/${_sessionId}`, data).then((response) => {
            mutateSession(_sessionId, response.data)
            return response.data
        })
    }

    const updateSessionStrides = (_sessionId, strides) => {
        return axios.put(`/sessions/${_sessionId}/strides`, strides).then((response) => {
            return response.data
        })
    }

    const deleteSession = (_sessionId) => {
        return axios.delete(`/sessions/${_sessionId}`).then((response) => {
            mutateSession(_sessionId, {})
            return response.data
        })
    }

    const splitSession = (_sessionId, data) => {
        return axios.post(`/sessions/${_sessionId}/split`, data).then((response) => {
            mutateSession(_sessionId, response.data)
            return response.data
        })
    }

    const reprocessSession = (_sessionId, data) => {
        return axios.post(`/sessions/${_sessionId}/process`, data).then(async (response) => {
            mutateSession(_sessionId, response.data)
            try {
                await checkPath3dAvailability(_sessionId, { forceRefresh: false })
            } catch (error) {
                console.warn('[useSessions] Failed to refresh path3d availability after reprocess', error)
            }
            return response.data
        })
    }

    const shareSession = (_sessionId, email) => {
        return axios.post(`/sessions/${_sessionId}/sharing/invite`, { email }).then((response) => {
            return response.data
        })
    }

    const unshareSession = (_sessionId, userId) => {
        return axios.delete(`/sessions/${_sessionId}/sharing/${userId}`).then((response) => {
            return response.data
        })
    }

    const transferOwnershipSession = (_sessionId, newOwnerId) => {
        return axios.put(`/sessions/${_sessionId}/owners`, { newOwnerId }).then((response) => {
            return response.data
        })
    }

    // S5: a member of the session's horse who may not see it asks its owners for access.
    // 201 → the request ({id, status: 'pending', …}); 409 when one already waits.
    const requestSessionAccess = (_sessionId) => {
        return axios.post(`/sessions/${_sessionId}/access-requests`).then((response) => {
            return response.data
        })
    }

    // The requester withdraws their pending request (an invitations row).
    const cancelSessionAccessRequest = (requestId) =>
        answerInvitation({ id: requestId, kind: KIND_REQUEST }, 'cancelled')

    // const sessionData = {
    //     session: ref(session),
    //     jumps: ref(jumps),
    //     strides: ref(strides),
    //     stridesStatistics: ref(stridesStatistics),
    // }

    const sensorHeight = computed(() => (session.value?.horse?.sensor_height ?? 0) / 100)
    const totalWeight = computed(() => getTotalWeight(session.value?.horse?.weight, [session.value?.rider]))

    const isError = computed(
        () =>
            !!error?.value ||
            (session?.value?.status && !['open', 'failed', 'processed', 'validation'].includes(session?.value?.status))
    )
    const isReady = computed(
        () =>
            trajectories?.value &&
            stridesStatistics?.value &&
            session?.value &&
            strides?.value &&
            jumps?.value &&
            !isError.value
    )
    const isLoading = computed(() => !!(!isReady.value && !isError.value))

    return {
        //data
        sessions,
        prefetchSession,
        lastSession,
        session,
        jumpsStatistics,
        sessionStatistics,
        heartrateStream,
        strides,
        stridesStatistics,
        jumps,
        trajectories,
        advices,
        isValidatingAdvices,
        isLoadingAdvices,
        sensorHeight,
        totalWeight,
        isError,
        isReady,
        isLoading,
        error,
        accessDenied,
        accessInfo,
        path3dSnapshot,
        path3dAvailable,
        //internal methods
        changeGaitOfSelection,
        updateStride,
        //methods
        updateSession,
        updateSessionStrides,
        deleteSession,
        splitSession,
        reprocessSession,
        mutateSession,
        mutateSessions,
        createSession,
        checkPath3dAvailability,
        fetchPath3dSnapshot,
        refreshPath3dSnapshot,
        refreshHeartrateStream,
        refreshSessionStatistics,
        shareSession,
        unshareSession,
        transferOwnershipSession,
        requestSessionAccess,
        cancelSessionAccessRequest,
        //ref parameter
        strideBeforeSelected,
        strideAfterSelected,
        page,
        sessionId,
        horseId,
        riderId,
        sessionType,
        sessionDiscipline,
    }
}
